/**
 * Track Entry Page — עמוד כניסה לאזור
 *
 * What an anonymous visitor sees at /pensia or /mashkanta: what the area is,
 * what they can ask, and two ways in — sign up free, or sign in.
 *
 * The URL is deliberately the SAME as the signed-in hub. A marketing URL that
 * differs from the app URL is the thing that breaks shared links, so
 * TrackRoot picks this component vs the hub based on auth state.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, MessageCircle, ShieldCheck } from 'lucide-react'
import api from '../../services/api'
import { TRACKS, type TrackKey } from '../../config/tracks'
import type { TrackPublic } from '../../types'

interface TrackEntryPageProps {
  track: TrackKey
}

/** Static reassurance points — the questions people ask before signing up. */
const PROMISES = [
  'חינם לגמרי, בלי התחייבות ובלי כרטיס אשראי',
  'בלי הסכם ייעוץ, בלי שאלון סיכון ובלי מסמכים',
  'הרשמה קצרה — שם, אימייל וסיסמה בלבד',
]

export default function TrackEntryPage({ track }: TrackEntryPageProps) {
  const config = TRACKS[track]
  const [info, setInfo] = useState<TrackPublic | null>(null)

  useEffect(() => {
    api
      .get<{ track: TrackPublic }>(`/tracks/${track}/info`)
      .then((res) => setInfo(res.data.track))
      // Falling back to the bundled copy keeps the page useful even if the API
      // is down — this is the public front door for the area.
      .catch(() => setInfo(null))
  }, [track])

  const name = info?.name || config.name
  const tagline = info?.tagline || config.tagline
  const heroText = info?.heroText || config.tagline

  return (
    <div className="min-h-screen bg-gradient-to-bl from-primary-50 via-white to-white" dir="rtl">
      <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-12 lg:grid-cols-2 lg:py-20">
        {/* Hero */}
        <section>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-800">
            <config.Icon size={16} aria-hidden="true" />
            {config.shortName}
          </span>

          <h1 className="mt-4 font-heading text-3xl font-bold text-gray-900 sm:text-4xl">{name}</h1>
          <p className="mt-3 text-lg text-gray-700">{tagline}</p>
          <p className="mt-4 text-base text-gray-700">{heroText}</p>

          <ul className="mt-8 space-y-3">
            {PROMISES.map((promise) => (
              <li key={promise} className="flex items-start gap-2 text-sm text-gray-800">
                <Check
                  size={18}
                  aria-hidden="true"
                  className="mt-0.5 flex-shrink-0 text-emerald-600"
                />
                {promise}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={config.signupPath} className="btn btn-primary">
              הרשמה חינם
            </Link>
            <Link to={`/login?next=${config.basePath}`} className="btn btn-outline">
              כבר יש לי חשבון
            </Link>
          </div>

          <p className="mt-6 text-xs text-gray-600">
            <ShieldCheck size={14} aria-hidden="true" className="ms-1 inline" />
            האזור מספק הסבר כללי בלבד ואינו מהווה ייעוץ אישי. להחלטות ממשיות כדאי להיוועץ
            ביועץ מורשה.
          </p>
        </section>

        {/* What you can ask — makes the value concrete instead of abstract */}
        <section
          className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
          aria-labelledby="entry-examples-heading"
        >
          <h2
            id="entry-examples-heading"
            className="flex items-center gap-2 font-heading text-lg font-bold text-gray-900"
          >
            <MessageCircle size={20} aria-hidden="true" className="text-primary-600" />
            מה אפשר לשאול כאן?
          </h2>
          <ul className="mt-4 space-y-3">
            {EXAMPLE_QUESTIONS[track].map((q) => (
              <li
                key={q}
                className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800"
              >
                {q}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-gray-600">
            התשובות הן הסבר כללי — לא המלצה על מוצר, גוף או מסלול מסוים.
          </p>
        </section>
      </div>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-5 text-xs text-gray-600">
          <Link to="/" className="underline">
            חזרה לעמוד הבית
          </Link>
          <nav aria-label="מידע משפטי" className="flex flex-wrap gap-4">
            <Link to="/privacy" className="underline">
              מדיניות פרטיות
            </Link>
            <Link to="/terms" className="underline">
              תנאי שימוש
            </Link>
            <Link to="/accessibility" className="underline">
              הצהרת נגישות
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  )
}

/**
 * Concrete examples beat a feature list. Kept here rather than in the DB because
 * they are marketing copy for the signed-OUT page — the signed-in suggested
 * questions are per-topic and admin-editable.
 */
const EXAMPLE_QUESTIONS: Record<TrackKey, string[]> = {
  student: [
    'מה ההבדל בין מניה לאגרת חוב?',
    'מה זה פיזור סיכונים ולמה הוא חשוב?',
    'איך ריבית דריבית עובדת?',
  ],
  retirement: [
    'מה ההבדל בין קרן פנסיה לביטוח מנהלים?',
    'מה זה מקדם המרה ולמה הוא משפיע על הקצבה?',
    'למה משיכת פיצויים היום פוגעת לי בפרישה?',
    'מה כדאי לבדוק בחמש השנים שלפני הפרישה?',
  ],
  mortgage: [
    'מה ההבדל בין ריבית קבועה למשתנה?',
    'מה הצמדה למדד עושה לחוב שלי?',
    'למה הארכת התקופה מוזילה את ההחזר ומייקרת את ההלוואה?',
    'מתי בכלל כדאי לבדוק מיחזור משכנתא?',
  ],
}
